/**
 * Knowledge Docs Service
 *
 * Fetches documents from kbase.knowledge_docs via the knowledge plugin API,
 * enriches them with Revisr library tags, and provides update/delete/analyze helpers.
 *
 * Used by KnowledgeBaseDocuments.tsx and boostPolicyService.ts.
 */

import { supabase } from '@foundation/utils/supabaseClient';
import { getActiveWorkspaceId } from '../../../header/activeWorkspace';
import type { AdminLibraryTag } from '../types/adminLibrary';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// ─── Types ────────────────────────────────────────────────────────────────────

export type KnowledgeDocMetadata = {
  title?: string;
  description?: string;
  status?: 'draft' | 'published';
  subCategory?: string;
  [key: string]: unknown;
};

export type KnowledgeDoc = {
  id: string;
  doc_name: string;
  store_id: string;
  org_id: string;
  user_id: string;
  category: string;
  visibility: string;
  tags: string[];
  metadata: KnowledgeDocMetadata;
  graph_status?: string;
  created_at: string;
  // Enriched after fetch
  revisrTags?: AdminLibraryTag[];
};

export type KnowledgeDocAnalysis = {
  category: string;
  tags: string[];
  chunking: {
    recommended: string;
    confidence: number;
    reasoning: string;
  };
  ontology: {
    entityTypes: Array<{ name: string; description: string }>;
    relationshipTypes: Array<{ name: string; fromType: string; toType: string }>;
  };
};

// ─── Auth helpers ─────────────────────────────────────────────────────────────

async function getAuthContext(): Promise<{ token: string; userId: string; orgId: string }> {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token ?? '';
  const userId = session?.user?.id ?? '';

  // Get the active workspace ID set by the WorkspaceSwitcher in the header
  const orgId = getActiveWorkspaceId() ?? '';

  return { token, userId, orgId };
}

// ─── Fetch knowledge docs ─────────────────────────────────────────────────────

export async function fetchKnowledgeDocs(): Promise<KnowledgeDoc[]> {
  const { token, userId, orgId } = await getAuthContext();

  // Use the Revisr knowledge-docs override endpoint which returns full KnowledgeDoc
  // objects (id, doc_name, tags, metadata, created_at, etc.).
  // The knowledge plugin's GET /api/knowledge/documents only returns { name, category,
  // visibility } which is missing the fields the Revisr Library UI needs.
  const response = await fetch(`${API_BASE}/api/revisr/knowledge-docs`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'x-user-id': userId,
      'x-org-id': orgId,
    },
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({})) as { error?: string };
    throw new Error(err.error ?? `Failed to fetch knowledge docs (${response.status})`);
  }

  return response.json() as Promise<KnowledgeDoc[]>;
}

// ─── Fetch Revisr tags by doc IDs ─────────────────────────────────────────────

export async function fetchRevisrTagsByDocIds(
  docIds: string[],
): Promise<Record<string, AdminLibraryTag[]>> {
  if (docIds.length === 0) return {};

  const { token, userId, orgId } = await getAuthContext();

  const url = new URL(`${API_BASE}/api/revisr/knowledge-tags/by-doc`);
  url.searchParams.set('doc_ids', docIds.join(','));

  const response = await fetch(url.toString(), {
    headers: {
      Authorization: `Bearer ${token}`,
      'x-user-id': userId,
      'x-org-id': orgId,
    },
  });

  if (!response.ok) return {};

  return response.json() as Promise<Record<string, AdminLibraryTag[]>>;
}

// ─── Fetch docs + enrich with Revisr tags ────────────────────────────────────

export async function fetchKnowledgeDocsWithTags(): Promise<KnowledgeDoc[]> {
  const docs = await fetchKnowledgeDocs();
  if (docs.length === 0) return docs;

  const tagsByDocId = await fetchRevisrTagsByDocIds(docs.map((d) => d.id));

  return docs.map((doc) => {
    const assignedTags = tagsByDocId[doc.id] ?? [];
    const assignedNames = new Set(assignedTags.map((t) => t.name.toLowerCase()));

    // Fold knowledge_docs.tags (plain strings) into revisrTags as 'general' entries,
    // skipping any name already covered by an assigned Revisr tag.
    const kbTagObjects: AdminLibraryTag[] = (doc.tags ?? [])
      .filter((name) => !assignedNames.has(name.toLowerCase()))
      .map((name) => ({
        id: `kbtag-${name}`,
        name,
        category: 'general' as AdminLibraryTag['category'],
        usage_count: 0,
        created_at: doc.created_at,
      }));

    return {
      ...doc,
      revisrTags: [...assignedTags, ...kbTagObjects],
    };
  });
}

// ─── Analyze document (AI chunking + category + tags) ────────────────────────

export async function analyzeKnowledgeDocument(
  sampleText: string,
  fileName: string,
  category?: string,
): Promise<KnowledgeDocAnalysis> {
  const { token, userId, orgId } = await getAuthContext();

  const response = await fetch(`${API_BASE}/api/knowledge/analyze`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      'x-user-id': userId,
      'x-org-id': orgId,
    },
    body: JSON.stringify({ sampleText, fileName, category }),
  });

  if (!response.ok) {
    throw new Error(`AI analysis failed (${response.status})`);
  }

  return response.json() as Promise<KnowledgeDocAnalysis>;
}

// ─── Update knowledge doc ─────────────────────────────────────────────────────

export async function updateKnowledgeDoc(
  docId: string,
  updates: {
    category?: string;
    visibility?: string;
    metadata?: Partial<KnowledgeDocMetadata>;
    tags?: string[];
    tag_ids?: string[];
  },
): Promise<void> {
  const { token, userId, orgId } = await getAuthContext();

  const response = await fetch(`${API_BASE}/api/revisr/knowledge-tags/docs/${docId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      'x-user-id': userId,
      'x-org-id': orgId,
    },
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({})) as { error?: string };
    throw new Error(err.error ?? `Failed to update document (${response.status})`);
  }
}

// ─── Delete knowledge doc ─────────────────────────────────────────────────────

export async function deleteKnowledgeDoc(docId: string): Promise<void> {
  const { token, userId, orgId } = await getAuthContext();

  // Use the org-kb endpoint which deletes by row id (UUID).
  // The knowledge plugin's DELETE /api/knowledge/documents/:id expects doc_name,
  // not UUID, causing "Document not found" errors.
  const response = await fetch(`${API_BASE}/api/revisr/org-kb/${docId}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
      'x-user-id': userId,
      'x-org-id': orgId,
    },
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({})) as { error?: string };
    throw new Error(err.error ?? `Failed to delete document (${response.status})`);
  }
}

// ─── Upload document ──────────────────────────────────────────────────────────

export async function uploadKnowledgeDocument(
  file: File,
  opts: {
    title: string;
    description?: string;
    category: string;
    tags?: string[];
    visibility: string;
    status?: 'draft' | 'published';
    orgIds?: string[];
  },
): Promise<{ id?: string; doc_id?: string }> {
  const { token, userId, orgId } = await getAuthContext();

  const formData = new FormData();
  formData.append('file', file);
  formData.append('storeId', orgId);
  formData.append('category', opts.category || 'general');
  formData.append('visibility', opts.visibility);
  formData.append(
    'metadata',
    JSON.stringify({
      title: opts.title,
      description: opts.description ?? '',
      status: opts.status ?? 'published',
      tags: opts.tags ?? [],
    }),
  );

  // Tags are assigned via Revisr tag assignments (knowledge_docs_tag_assignments)
  // after upload — do NOT also send them here to avoid duplicates in knowledge_docs.tags

  // Always send orgIds so the backend upload endpoint routes correctly.
  // Without this, isGlobalUpload stays false and targetOrgIds stays [] —
  // the storage loop runs 0 times and nothing is stored despite a 200 response.
  if (opts.visibility === 'global') {
    // Empty array triggers isGlobalUpload = true in the backend (org_id = NULL, visible to all)
    formData.append('orgIds', JSON.stringify([]));
  } else if (opts.orgIds && opts.orgIds.length > 0) {
    formData.append('orgIds', JSON.stringify(opts.orgIds));
  } else {
    // org/private: store under the user's own org
    formData.append('orgIds', JSON.stringify([orgId]));
  }

  const response = await fetch(`${API_BASE}/api/knowledge/documents/upload`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'x-user-id': userId,
      'x-org-id': orgId,
    },
    body: formData,
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({})) as { error?: string };
    throw new Error(err.error ?? `Upload failed (${response.status})`);
  }

  // The upload endpoint does not return the new document's UUID in its response.
  // The upload stores synchronously before responding, so the doc will be in the
  // DB by the time we query. Look it up so callers can assign Revisr tags.
  await response.json().catch(() => null); // consume response body

  // Attempt lookup; retry once after a brief wait in case of DB propagation delay.
  let docsAfterUpload = await fetchKnowledgeDocs().catch(() => [] as KnowledgeDoc[]);
  let uploaded = docsAfterUpload.find((d) => d.doc_name === file.name);

  if (!uploaded) {
    await new Promise((resolve) => setTimeout(resolve, 1500));
    docsAfterUpload = await fetchKnowledgeDocs().catch(() => [] as KnowledgeDoc[]);
    uploaded = docsAfterUpload.find((d) => d.doc_name === file.name);
  }

  return {
    id: uploaded?.id,
    doc_id: uploaded?.id,
  };
}

// ─── Map KnowledgeDoc to BoostPolicyKnowledgeDocument shape ──────────────────

export function mapToBoostPolicyDoc(doc: KnowledgeDoc) {
  const title = doc.metadata?.title || doc.doc_name;
  const description = (doc.metadata?.description as string) || '';
  const allTags = [
    ...(doc.tags ?? []),
    ...(doc.revisrTags?.map((t) => t.name) ?? []),
  ];
  const uniqueTags = [...new Set(allTags)];
  const ext = doc.doc_name.split('.').pop() ?? 'document';

  return {
    id: doc.id,
    title,
    description,
    tags: uniqueTags,
    fileType: ext,
  };
}
